import { useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AuthCard } from '../components/common/AuthCard';
import { FormAlert, SubmitButton, TextField, vEmail } from '../components/form';
import { post, statusOf } from '../lib/api';
import { passwordFeedbackKey } from '../lib/passwordFeedback';
import { Paths } from '../routes/paths';
import { usePageTitle } from '../lib/usePageTitle';

const schema = z.object({ email: vEmail });
type Values = z.infer<typeof schema>;

export function ForgotPasswordPage() {
    const { t } = useTranslation();
    usePageTitle(t('forgot.title'));
    const methods = useForm<Values>({
        resolver: zodResolver(schema),
        defaultValues: { email: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });
    const [serverError, setServerError] = useState('');
    const [sent, setSent] = useState(false);

    async function onSubmit(values: Values) {
        setServerError('');
        try {
            await post('login/ResetPasswordWithToken', { email: values.email });
            setSent(true);
        } catch (err) {
            setServerError(t(passwordFeedbackKey(statusOf(err))));
        }
    }

    if (sent) {
        return (
            <AuthCard title={t('forgot.title')}>
                <Alert variant="success">{t('forgot.success')}</Alert>
                <Link to={Paths.login} className="btn btn-primary">
                    {t('common.backToLogin')}
                </Link>
            </AuthCard>
        );
    }

    return (
        <AuthCard title={t('forgot.title')} subtitle={t('forgot.subtitle')}>
            <FormProvider {...methods}>
                <FormAlert onClose={() => setServerError('')}>{serverError || undefined}</FormAlert>
                <Form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
                    <TextField
                        name="email"
                        label={t('forgot.email')}
                        type="email"
                        placeholder={t('login.usernamePlaceholder')}
                        autoComplete="email"
                        required
                    />
                    <SubmitButton loading={methods.formState.isSubmitting}>{t('forgot.submit')}</SubmitButton>
                </Form>
            </FormProvider>
        </AuthCard>
    );
}
